// Task 24498 - "seems broken as development use only", reported on the map on
// /staff-details/:id/Overview.
//
// It was not a bug in that screen. Google draws its grey "For development
// purposes only" watermark across a map whenever the key's Cloud project fails
// authorisation, and the commonest reason is billing:
//
//   GET /maps/api/geocode/json?key=...
//   { "status": "REQUEST_DENIED",
//     "error_message": "You must enable Billing on the Google Cloud Project..." }
//
// The problem with that failure is not the watermark, it is the SILENCE. The
// map still renders, nothing throws, nothing is logged, and every map and
// address lookup in the product degrades at once - the staff map, the customer
// overview map, the dispatch preview, the vehicle GPS tab, and the address
// autocompletes on jobs and tasks. It reads as one broken screen, which is how
// it was reported, when it is actually one account-level setting.
//
// Google calls window.gm_authFailure when that happens. Nothing was listening,
// so this registers a single handler: it records the failure and toasts once,
// and map components ask hasGoogleMapsAuthFailed() so they can say what is
// wrong instead of showing a watermark.

import { toast } from "react-toastify";

let authFailed = false;
let registered = false;
const listeners = new Set();

export const hasGoogleMapsAuthFailed = () => authFailed;

/**
 * Subscribe to the failure. Returns an unsubscribe, so a component can react
 * without polling - the failure arrives asynchronously, after the map has
 * already rendered.
 */
export const onGoogleMapsAuthFailure = (listener) => {
  listeners.add(listener);
  if (authFailed) listener();
  return () => listeners.delete(listener);
};

/**
 * Registered once, from App. Google replaces whatever is on
 * window.gm_authFailure, so this deliberately does not chain onto an existing
 * handler - there is only ever meant to be one.
 */
export const registerGoogleMapsAuthFailureHandler = () => {
  if (registered || typeof window === "undefined") return;
  registered = true;

  window.gm_authFailure = () => {
    if (authFailed) return;
    authFailed = true;

    // Named plainly, because the person seeing it is the person who can fix
    // it, and "maps failed to load" would send them looking at the code.
    toast.error(
      "Google Maps could not authorise. Maps and address lookups will not work until billing is enabled on the Google Cloud project for the Maps API key.",
      { autoClose: false, toastId: "google-maps-auth-failure" }
    );
    // eslint-disable-next-line no-console
    console.error(
      "[GoogleMaps] gm_authFailure - the Maps API key was rejected. Usual causes: billing not enabled on the Cloud project, the Maps JavaScript API not enabled, or an HTTP-referrer restriction that does not cover this host."
    );

    listeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        /* a bad listener must not stop the others being told */
      }
    });
  };
};
